import React, { useEffect, useMemo, useState } from "react";
import InstitutionBadge from "./InstitutionBadge";

const STEP_META = [
  ["pathway", "ConfigurationPathway"],
  ["assignment", "ConfigurationAssignment"],
  ["projection", "ProjectionCandidate"],
  ["atom", "RelationalAtom"],
  ["source", "SourceMatter"],
  ["root", "StudyRoot"],
];

function first(object, keys, fallback = null) {
  if (!object || typeof object !== "object") return fallback;
  for (const key of keys) {
    if (object[key] !== undefined && object[key] !== null) return object[key];
  }
  return fallback;
}

function compact(value = "", limit = 1000) {
  const clean = String(value || "").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  return clean.length > limit ? `${clean.slice(0, limit - 1).trim()}…` : clean;
}

function normalize(detail = {}, rootDetail = null) {
  return {
    pathway: detail?.pathway || detail?.configuration_pathway || {},
    assignment: detail?.assignment || detail?.configuration_assignment || {},
    projection: detail?.projection || detail?.projection_candidate || {},
    atom: detail?.relational_atom || detail?.atom || {},
    source: detail?.source_matter || detail?.source || {},
    root: rootDetail || detail?.study_root || detail?.root || {},
  };
}

function identifier(key, object = {}) {
  const keys = {
    pathway: ["id", "pathway_id"],
    assignment: ["id", "assignment_id"],
    projection: ["id", "projection_id"],
    atom: ["id", "atom_id", "relational_atom_id"],
    source: ["id", "source_matter_id"],
    root: ["root_id", "id", "study_root_id"],
  }[key] || ["id"];
  return first(object, keys, "");
}

function Receipt({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span className="rr-prov-receipt">
      <small>{label}</small>
      <b>{String(value)}</b>
    </span>
  );
}

function RelationView({ object }) {
  const source = first(object, ["source_phrase", "source", "subject_phrase"], "");
  const relation = first(object, ["relation", "relation_family", "predicate"], "");
  const target = first(object, ["target_phrase", "target", "object_phrase"], "");
  if (!source && !relation && !target) return null;

  return (
    <div className="rr-prov-relation">
      <div>
        <span className="v25a-eyebrow">SOURCE PHRASE</span>
        <b>{source || "source phrase unavailable"}</b>
      </div>
      <i>
        <small>{relation || "relates to"}</small>
        →
      </i>
      <div>
        <span className="v25a-eyebrow">TARGET PHRASE</span>
        <b>{target || "target phrase unavailable"}</b>
      </div>
    </div>
  );
}

function Detail({ activeKey, object }) {
  if (activeKey === "pathway") {
    return (
      <>
        <span className="v25a-eyebrow">CONFIGURATION PATHWAY</span>
        <code>{identifier(activeKey, object)}</code>
        <div className="rr-prov-receipts">
          <Receipt label="orientation" value={first(object, ["contribution_orientation", "orientation"])} />
          <Receipt label="warrant" value={first(object, ["contribution_warrant", "warrant"])} />
          <Receipt label="root" value={first(object, ["root_id", "study_root_id"])} />
        </div>
        <p className="rr-prov-definition">
          A configuration-relative relational reading that has passed the runtime warrant boundary. It is not itself a source passage or causal claim.
        </p>
      </>
    );
  }

  if (activeKey === "assignment") {
    return (
      <>
        <span className="v25a-eyebrow">CONFIGURATION ASSIGNMENT</span>
        <code>{identifier(activeKey, object)}</code>
        <h4>{first(object, ["resolution", "assignment_status", "status"], "Competitive assignment")}</h4>
        <div className="rr-prov-receipts">
          <Receipt label="specificity" value={first(object, ["specificity_score", "specificity"])} />
          <Receipt label="winner margin" value={first(object, ["winner_margin", "margin"])} />
        </div>
        <p className="rr-prov-definition">
          Resolves which configuration receives the projection. Route membership alone does not create this assignment.
        </p>
      </>
    );
  }

  if (activeKey === "projection") {
    return (
      <>
        <span className="v25a-eyebrow">PROJECTION CANDIDATE</span>
        <code>{identifier(activeKey, object)}</code>
        <h4>{first(object, ["status", "projection_status"], "Route-supported projection candidate")}</h4>
        <div className="rr-prov-receipts">
          <Receipt label="orientation" value={first(object, ["orientation"])} />
          <Receipt label="route" value={first(object, ["route", "route_label"])} />
        </div>
        <p className="rr-prov-definition">
          A candidate interpretation projected from source-near relational material. It does not become a pathway merely by existing.
        </p>
      </>
    );
  }

  if (activeKey === "atom") {
    return (
      <>
        <span className="v25a-eyebrow">RELATIONAL ATOM</span>
        <code>{identifier(activeKey, object)}</code>
        <RelationView object={object} />
        <div className="rr-prov-receipts">
          <Receipt label="parse distance" value={first(object, ["parse_distance"])} />
          <Receipt label="relation warrant" value={first(object, ["relation_warrant", "warrant"])} />
        </div>
        <p className="rr-prov-definition">
          The source-near relational unit. The interface keeps this relation visible rather than replacing it with the compression word “evidence”.
        </p>
      </>
    );
  }

  if (activeKey === "source") {
    const passage = first(object, ["text", "passage", "source_text", "source_matter"], "");
    return (
      <>
        <span className="v25a-eyebrow">SOURCE MATTER · DISTANCE 0</span>
        <code>{identifier(activeKey, object)}</code>
        <blockquote>{compact(passage) || "SourceMatter text was not returned in this payload."}</blockquote>
        <div className="rr-prov-receipts">
          <Receipt label="source distance" value={first(object, ["source_distance"], 0)} />
          <Receipt label="source type" value={first(object, ["source_type", "kind", "type"])} />
        </div>
        <p className="rr-prov-definition">Immutable source-near substrate used by the analytical reading.</p>
      </>
    );
  }

  const title = first(object, ["title", "display_name"], "StudyRoot");
  const authors = first(object, ["authors", "author_names"], []);
  const year = first(object, ["year", "publication_year"], "");
  const doi = first(object, ["doi"], "");

  return (
    <>
      <span className="v25a-eyebrow">STUDY ROOT</span>
      <code>{identifier(activeKey, object)}</code>
      <h4>{title}</h4>
      <InstitutionBadge root={object} />
      <div className="rr-prov-root-meta">
        {Array.isArray(authors) && authors.length ? <span>{authors.slice(0, 6).join(", ")}</span> : null}
        {typeof authors === "string" && authors ? <span>{authors}</span> : null}
        {year ? <span>{year}</span> : null}
        {doi ? <span>DOI {doi}</span> : null}
      </div>
      <p className="rr-prov-definition">
        Study-level provenance container. Institution badges are shown only when explicit affiliation or institution metadata is returned with this root.
      </p>
    </>
  );
}

export default function ProvenanceModal({ open, detail, rootDetail, stateLabel, onClose }) {
  const objects = useMemo(() => normalize(detail, rootDetail), [detail, rootDetail]);
  const steps = useMemo(
    () => STEP_META.map(([key, label]) => ({
      key,
      label,
      available: Boolean(objects[key] && Object.keys(objects[key]).length),
    })),
    [objects]
  );

  const firstAvailable = steps.find((step) => step.available)?.key || "pathway";
  const [activeKey, setActiveKey] = useState(firstAvailable);

  useEffect(() => {
    if (open) setActiveKey(firstAvailable);
  }, [open, firstAvailable]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => event.key === "Escape" && onClose?.();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const activeIndex = Math.max(0, steps.findIndex((step) => step.key === activeKey));
  const root = objects.root || {};
  const pathway = objects.pathway || {};

  function move(delta) {
    let next = activeIndex + delta;
    while (next >= 0 && next < steps.length) {
      if (steps[next].available) {
        setActiveKey(steps[next].key);
        return;
      }
      next += delta;
    }
  }

  return (
    <div className="rr-prov-modal-layer">
      <button className="rr-prov-backdrop" type="button" aria-label="Close provenance" onClick={onClose} />

      <section className="rr-prov-modal" role="dialog" aria-modal="true" aria-label="Provenance trace">
        <header className="rr-prov-head">
          <div>
            <span className="v25a-eyebrow">PROVENANCE TRACE</span>
            <h2>How this explanation is earned</h2>
            {stateLabel ? <p>{stateLabel}</p> : null}
          </div>
          <div className="rr-prov-head-actions">
            <InstitutionBadge root={root} compact />
            <button type="button" onClick={onClose}>Lukk ×</button>
          </div>
        </header>

        <div className="rr-prov-boundaries">
          <span>ROOT-NORMALIZED</span>
          <span>CAUSALITY NOT CLAIMED</span>
          <span>SOURCE MATTER · DISTANCE 0</span>
        </div>

        <div className="rr-prov-summary">
          <div><small>PATHWAY</small><b>{first(pathway, ["contribution_orientation", "orientation"], "—")}</b></div>
          <div><small>WARRANT</small><b>{first(pathway, ["contribution_warrant", "warrant"], "—")}</b></div>
          <div><small>SELECTED ROOT</small><b>{identifier("root", root) || "—"}</b></div>
          <div><small>ACTIVE OBJECT</small><b>{steps[activeIndex]?.label}</b></div>
        </div>

        <nav className="rr-prov-rail" aria-label="Underlying analytical provenance">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <button
                type="button"
                disabled={!step.available}
                className={`${activeKey === step.key ? "active" : ""} ${step.available ? "" : "muted"}`}
                onClick={() => step.available && setActiveKey(step.key)}
              >
                <span>{index + 1}</span>
                <b>{step.label}</b>
              </button>
              {index < steps.length - 1 ? <i>→</i> : null}
            </React.Fragment>
          ))}
        </nav>

        <div className="rr-prov-body">
          <section className="rr-prov-detail">
            <Detail activeKey={activeKey} object={objects[activeKey] || {}} />
          </section>

          <aside className="rr-prov-side">
            <div className="rr-prov-minimap">
              <span className="v25a-eyebrow">TRACE MAP</span>
              {steps.map((step, index) => (
                <React.Fragment key={step.key}>
                  <button
                    type="button"
                    disabled={!step.available}
                    className={activeKey === step.key ? "active" : ""}
                    onClick={() => step.available && setActiveKey(step.key)}
                  >
                    {index + 1}
                  </button>
                  {index < steps.length - 1 ? <i /> : null}
                </React.Fragment>
              ))}
            </div>

            <div className="rr-prov-language-note">
              <span className="v25a-eyebrow">UNDERLYING LANGUAGE</span>
              <p>
                “Evidence” is a navigation compression only. The explicit chain remains ConfigurationPathway → ConfigurationAssignment → ProjectionCandidate → RelationalAtom → SourceMatter → StudyRoot.
              </p>
            </div>
          </aside>
        </div>

        <footer className="rr-prov-foot">
          <button type="button" disabled={activeIndex <= 0} onClick={() => move(-1)}>← Previous object</button>
          <span>{activeIndex + 1} / {steps.length} · {steps[activeIndex]?.label}</span>
          <button type="button" disabled={activeIndex >= steps.length - 1} onClick={() => move(1)}>Next object →</button>
        </footer>
      </section>
    </div>
  );
}
